import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import GlobalCurrencyBanner from '@/components/GlobalCurrencyBanner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { authAPI, transactionsAPI, recipientsAPI } from '@/lib/api';
import { Send, Users, History, ArrowRight, DollarSign, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userRes, txRes, recipientsRes] = await Promise.all([
        authAPI.getMe(),
        transactionsAPI.getAll(),
        recipientsAPI.getAll()
      ]);
      setUser(userRes.data);
      setTransactions(txRes.data.slice(0, 5));
      setRecipients(recipientsRes.data.slice(0, 3));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-accent';
      case 'funds_sent': return 'text-sky-500';
      case 'payment_processed': return 'text-accent';
      case 'pending': return 'text-yellow-600';
      case 'awaiting_funds': return 'text-amber-500';
      case 'processing': return 'text-blue-500';
      case 'failed': return 'text-destructive';
      case 'canceled': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'funds_sent': 'Funds Sent',
      'awaiting_funds': 'Awaiting Funds',
      'payment_processed': 'Completed',
      'completed': 'Completed',
      'pending': 'Pending',
      'processing': 'Processing',
      'failed': 'Failed',
      'canceled': 'Canceled',
    };
    return labels[status] || status;
  };

  const MANAGEMENT_ROLES = ['super_admin', 'admin', 'compliance_officer', 'coo', 'cfo', 'cto'];
  const isManagement = user && (user.is_admin || MANAGEMENT_ROLES.includes(user.role));
  const isKYCPending = user && !isManagement && (!user.kyc_status || user.kyc_status === 'pending' || user.kyc_status === 'pending_bridge');
  const isKYCBlocked = user && user.kyc_status === 'blocked';

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <GlobalCurrencyBanner />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* KYC Alert Banner */}
        {isKYCPending && (
          <div data-testid="kyc-alert" className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium text-amber-900">Complete Your KYC Verification</p>
                <p className="text-sm text-amber-700/80">Verify your identity to access all features and start sending money.</p>
              </div>
            </div>
            <Button onClick={() => navigate('/kyc')} className="bg-amber-500 hover:bg-amber-600 text-black">
              Complete KYC
            </Button>
          </div>
        )}

        {isKYCBlocked && (
          <div data-testid="kyc-blocked-alert" className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <div>
              <p className="font-medium text-red-100">Account Restricted</p>
              <p className="text-sm text-red-300/70">Your account has been restricted. Please contact support for assistance.</p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Welcome back, {user?.name}!</h1>
          <p className="text-muted-foreground">Manage your money transfers and recipients</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stat-card-balance" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground uppercase tracking-[0.2em]">Balance</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">$0.00</p>
          </Card>

          <Card data-testid="stat-card-transactions" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <History className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground uppercase tracking-[0.2em]">Transactions</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">{transactions.length}</p>
          </Card>

          <Card data-testid="stat-card-recipients" className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-accent" />
              <span className="text-sm text-muted-foreground uppercase tracking-[0.2em]">Recipients</span>
            </div>
            <p className="text-3xl font-bold font-mono tabular-nums">{recipients.length}</p>
          </Card>

          <Card data-testid="quick-send-card" className="p-6 bg-accent text-white hover:bg-accent/90 transition-all cursor-pointer" onClick={() => navigate('/send')}>
            <div className="flex items-center gap-3 mb-2">
              <Send className="w-5 h-5" />
              <span className="text-sm uppercase tracking-[0.2em]">Quick Send</span>
            </div>
            <p className="text-2xl font-semibold">Send Money</p>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card data-testid="recent-transactions-card" className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Recent Transactions</h2>
              <Button data-testid="view-all-transactions-btn" variant="ghost" size="sm" onClick={() => navigate('/transactions')} className="gap-2">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            {transactions.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No transactions yet</p>
            ) : (
              <div className="space-y-4">
                {transactions.map((tx) => (
                  <div key={tx.id} data-testid={`transaction-item-${tx.id}`} className="flex justify-between items-center pb-4 border-b last:border-0">
                    <div>
                      <p className="font-medium">
                        {tx.provider === 'bridge' ? `${tx.transfer_type === 'onramp' ? 'USD → USDC' : tx.from_currency + ' → ' + tx.to_currency}` : `${tx.from_currency} → ${tx.to_currency}`}
                      </p>
                      <p className="text-sm text-muted-foreground">{formatDate(tx.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-semibold">${tx.amount?.toFixed(2)}</p>
                      <p className={`text-sm capitalize ${getStatusColor(tx.state || tx.status)}`}>
                        {getStatusLabel(tx.state || tx.status)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card data-testid="saved-recipients-card" className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold">Saved Recipients</h2>
              <Button data-testid="view-all-recipients-btn" variant="ghost" size="sm" onClick={() => navigate('/recipients')} className="gap-2">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            {recipients.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No recipients added yet</p>
                <Button data-testid="add-first-recipient-btn" onClick={() => navigate('/recipients')} variant="accent">
                  Add Your First Recipient
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recipients.map((recipient) => (
                  <div key={recipient.id} data-testid={`recipient-item-${recipient.id}`} className="flex justify-between items-center pb-4 border-b last:border-0">
                    <div>
                      <p className="font-medium">{recipient.name}</p>
                      <p className="text-sm text-muted-foreground">{recipient.country}</p>
                    </div>
                    <Button data-testid={`send-to-recipient-${recipient.id}`} size="sm" onClick={() => navigate('/send')} variant="accent">
                      Send
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}